<template>
    <div
        ref="selectRef"
        class="v-select"
        :class="{ 'vs--open': isOpen, 'vs--disabled': disabled }"
        @keydown.escape="closeDropdown"
    >
        <div
            class="vs__dropdown-toggle"
            @click="toggleDropdown"
        >
            <div class="vs__selected-options">
                <div
                    v-if="multiple"
                    class="vs__selected-options-container"
                >
                    <span
                        v-for="option in selectedOptions"
                        :key="getOptionValue(option)"
                        class="vs__selected"
                    >
                        {{ getOptionLabel(option) }}
                        <button
                            v-if="clearable"
                            @click.stop="removeOption(option)"
                            class="vs__deselect"
                            type="button"
                            :aria-label="`Remove ${getOptionLabel(option)}`"
                        >
                            ×
                        </button>
                    </span>
                    <span
                        v-if="!hasValue"
                        class="vs__placeholder"
                    >
                        {{ computedPlaceholder }}
                    </span>
                </div>
                <div
                    v-else
                    class="vs__selected"
                    :class="{ vs__placeholder: !hasValue }"
                >
                    {{ selectedLabel || computedPlaceholder }}
                </div>
            </div>

            <div class="vs__actions">
                <button
                    v-if="clearable && hasValue"
                    @click.stop="clearSelection"
                    class="vs__clear"
                    type="button"
                    aria-label="Clear selection"
                >
                    ×
                </button>
                <div
                    v-if="loading || isCreatingTag"
                    class="vs__loading"
                >
                    ⏳
                </div>
                <div
                    v-else
                    class="vs__open-indicator"
                    aria-hidden="true"
                >
                    ▼
                </div>
            </div>
        </div>

        <!-- Dropdown positioned relative to parent -->
        <Transition name="vs-dropdown">
            <div
                v-show="isOpen"
                ref="dropdownRef"
                class="vs__dropdown-menu"
            >
                <div
                    v-if="searchable"
                    class="vs__search-box"
                >
                    <input
                        ref="searchInputRef"
                        v-model="searchQuery"
                        class="vs__search"
                        :placeholder="searchPlaceholder || 'Search...'"
                        @input="handleSearchInput"
                        @keydown.enter.prevent="selectHighlighted"
                        @keydown.arrow-down.prevent="navigateOptions(1)"
                        @keydown.arrow-up.prevent="navigateOptions(-1)"
                        @keydown.tab="handleTab"
                        autocomplete="off"
                        spellcheck="false"
                    />
                </div>

                <div
                    ref="optionsRef"
                    class="vs__dropdown-options"
                    @scroll="handleScroll"
                >
                    <div
                        v-for="(option, index) in filteredOptions"
                        :key="`${getOptionValue(option)}-${index}`"
                        class="vs__dropdown-option"
                        :class="{
                            'vs__dropdown-option--highlight': index === highlightedIndex,
                            'vs__dropdown-option--selected': isSelected(option),
                            'vs__dropdown-option--create': option.__isCreateOption,
                        }"
                        @click="selectOption(option)"
                        @mouseenter="highlightedIndex = index"
                        role="option"
                        :aria-selected="isSelected(option)"
                    >
                        <slot
                            name="option"
                            :option="option"
                        >
                            {{ getOptionLabel(option) }}
                        </slot>
                    </div>

                    <div
                        v-if="loading && loadingText"
                        class="vs__loading-text"
                    >
                        {{ loadingText }}
                    </div>

                    <div
                        v-if="!loading && filteredOptions.length === 0"
                        class="vs__no-options"
                    >
                        <slot name="no-options">
                            {{ noOptionsText }}
                        </slot>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

export default {
    name: 'VSelect',
    props: {
        modelValue: {
            type: [String, Number, Array, Object, Boolean],
            default: null,
        },
        options: {
            type: Array,
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        searchPlaceholder: {
            type: String,
            default: '',
        },
        noOptionsText: {
            type: String,
            default: 'No options available',
        },
        loadingText: {
            type: String,
            default: 'Loading...',
        },
        label: {
            type: String,
            default: 'label',
        },
        value: {
            type: String,
            default: 'value',
        },
        reduce: {
            type: Function,
            default: null,
        },
        allowCreate: {
            type: Boolean,
            default: false,
        },
        remote: {
            type: Boolean,
            default: false,
        },
        remoteMethod: {
            type: Function,
            default: null,
        },
        onCreateTag: {
            type: Function,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        debounce: {
            type: Number,
            default: 250,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
    },
    emits: [
        'update:modelValue',
        'option:selected',
        'option:deselected',
        'tag:created',
        'search',
        'open',
        'close',
        'clear',
    ],
    setup(props, { emit }) {
        // Refs
        const selectRef = ref(null);
        const dropdownRef = ref(null);
        const searchInputRef = ref(null);
        const optionsRef = ref(null);
        const isOpen = ref(false);
        const searchQuery = ref('');
        const highlightedIndex = ref(-1);
        const isCreatingTag = ref(false);
        let searchTimeout = null;

        // Computed
        const selectedOptions = computed(() => {
            if (props.multiple) {
                if (!Array.isArray(props.modelValue)) return [];
                return props.modelValue
                    .map((val) => findOptionByValue(val))
                    .filter(Boolean);
            }
            return props.modelValue ? [findOptionByValue(props.modelValue)] : [];
        });

        const selectedLabel = computed(() => {
            if (props.multiple) return '';
            const option = findOptionByValue(props.modelValue);
            return option ? getOptionLabel(option) : '';
        });

        const hasValue = computed(() => {
            if (props.multiple) {
                return Array.isArray(props.modelValue) && props.modelValue.length > 0;
            }
            return (
                props.modelValue !== null &&
                props.modelValue !== undefined &&
                props.modelValue !== ''
            );
        });

        const computedPlaceholder = computed(() => {
            return props.placeholder || 'Select...';
        });

        const filteredOptions = computed(() => {
            let opts = [...(props.options || [])];

            // Filter by search query if not remote
            if (!props.remote && searchQuery.value) {
                const query = searchQuery.value.toLowerCase();
                opts = opts.filter((option) => {
                    const label = getOptionLabel(option);
                    return label && label.toLowerCase().includes(query);
                });
            }

            // Add create option
            if (props.allowCreate && searchQuery.value && searchQuery.value.trim()) {
                const searchLower = searchQuery.value.toLowerCase();
                const hasMatch = opts.some((option) => {
                    const label = getOptionLabel(option);
                    return label && label.toLowerCase() === searchLower;
                });

                if (!hasMatch) {
                    opts.unshift({
                        [props.label]: `Create "${searchQuery.value}"`,
                        [props.value]: searchQuery.value,
                        __isCreateOption: true,
                    });
                }
            }

            return opts;
        });

        // Methods
        const getOptionLabel = (option) => {
            if (!option) return '';
            if (typeof option === 'string' || typeof option === 'number') {
                return String(option);
            }
            return option[props.label] || option.label || option.name || String(option);
        };

        const getOptionValue = (option) => {
            if (!option) return null;
            if (typeof option === 'string' || typeof option === 'number') {
                return option;
            }
            if (props.reduce) {
                return props.reduce(option);
            }
            return option[props.value] || option;
        };

        const findOptionByValue = (value) => {
            if (!props.options || !Array.isArray(props.options)) return null;
            return props.options.find((option) => getOptionValue(option) === value);
        };

        const isSelected = (option) => {
            const optionValue = getOptionValue(option);
            if (props.multiple) {
                return Array.isArray(props.modelValue) &&
                    props.modelValue.includes(optionValue);
            }
            return props.modelValue === optionValue;
        };

        const toggleDropdown = () => {
            if (props.disabled) return;
            isOpen.value ? closeDropdown() : openDropdown();
        };

        const openDropdown = async () => {
            isOpen.value = true;
            emit('open');

            await nextTick();

            if (props.searchable && searchInputRef.value) {
                searchInputRef.value.focus();
            }

            // Highlight first selected option or first option
            const selectedIdx = filteredOptions.value.findIndex(isSelected);
            highlightedIndex.value = selectedIdx >= 0 ? selectedIdx : 0;
        };

        const closeDropdown = () => {
            isOpen.value = false;
            highlightedIndex.value = -1;
            if (!props.remote) {
                searchQuery.value = '';
            }
            emit('close');
        };

        const selectOption = async (option) => {
            // Handle tag creation
            if (option.__isCreateOption) {
                await handleCreateTag();
                return;
            }

            const optionValue = getOptionValue(option);

            if (props.multiple) {
                const currentValue = Array.isArray(props.modelValue)
                    ? [...props.modelValue]
                    : [];

                if (currentValue.includes(optionValue)) {
                    const newValue = currentValue.filter((val) => val !== optionValue);
                    emit('update:modelValue', newValue);
                    emit('option:deselected', option);
                } else {
                    const newValue = [...currentValue, optionValue];
                    emit('update:modelValue', newValue);
                    emit('option:selected', option);
                }

                // Clear search after selection in multiple mode
                if (!props.remote) {
                    searchQuery.value = '';
                }
            } else {
                emit('update:modelValue', optionValue);
                emit('option:selected', option);

                if (props.closeOnSelect) {
                    closeDropdown();
                }
            }
        };

        const removeOption = (option) => {
            if (!props.multiple) return;

            const currentValue = Array.isArray(props.modelValue)
                ? [...props.modelValue]
                : [];
            const optionValue = getOptionValue(option);
            const newValue = currentValue.filter((val) => val !== optionValue);

            emit('update:modelValue', newValue);
            emit('option:deselected', option);
        };

        const clearSelection = () => {
            emit('update:modelValue', props.multiple ? [] : null);
            emit('clear');
        };

        const handleCreateTag = async () => {
            const newTag = searchQuery.value.trim();
            if (!newTag) return;

            try {
                isCreatingTag.value = true;

                let savedTag = newTag;
                if (props.onCreateTag) {
                    savedTag = await props.onCreateTag(newTag);
                }

                emit('tag:created', savedTag);

                // Add to selection
                if (props.multiple) {
                    const currentValue = Array.isArray(props.modelValue)
                        ? [...props.modelValue]
                        : [];
                    if (!currentValue.includes(savedTag)) {
                        emit('update:modelValue', [...currentValue, savedTag]);
                    }
                } else {
                    emit('update:modelValue', savedTag);
                    if (props.closeOnSelect) {
                        closeDropdown();
                    }
                }

                searchQuery.value = '';
            } catch (error) {
                console.error('Error creating tag:', error);
            } finally {
                isCreatingTag.value = false;
            }
        };

        const selectHighlighted = () => {
            if (highlightedIndex.value >= 0 && filteredOptions.value[highlightedIndex.value]) {
                selectOption(filteredOptions.value[highlightedIndex.value]);
            }
        };

        const navigateOptions = (direction) => {
            const total = filteredOptions.value.length;
            if (total === 0) return;

            if (direction > 0) {
                highlightedIndex.value = (highlightedIndex.value + 1) % total;
            } else {
                highlightedIndex.value = highlightedIndex.value <= 0
                    ? total - 1
                    : highlightedIndex.value - 1;
            }

            // Scroll highlighted option into view
            nextTick(() => {
                const highlighted = optionsRef.value?.children[highlightedIndex.value];
                if (highlighted) {
                    highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            });
        };

        const handleTab = (event) => {
            if (isOpen.value) {
                selectHighlighted();
                event.preventDefault();
            }
        };

        const handleScroll = () => {
            // Handle scroll events if needed
        };

        const handleSearchInput = () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }

            if (props.remote && props.remoteMethod) {
                searchTimeout = setTimeout(() => {
                    props.remoteMethod(searchQuery.value);
                    emit('search', searchQuery.value);
                }, props.debounce);
            } else {
                emit('search', searchQuery.value);
            }
        };

        // Click outside handler
        const handleClickOutside = (event) => {
            if (selectRef.value && !selectRef.value.contains(event.target)) {
                closeDropdown();
            }
        };

        // Lifecycle
        onMounted(() => {
            document.addEventListener('click', handleClickOutside);

            if (props.remote && props.remoteMethod && !props.options?.length) {
                props.remoteMethod('');
            }
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        });

        return {
            // Refs
            selectRef,
            dropdownRef,
            searchInputRef,
            optionsRef,
            isOpen,
            searchQuery,
            highlightedIndex,
            isCreatingTag,

            // Computed
            selectedOptions,
            selectedLabel,
            hasValue,
            computedPlaceholder,
            filteredOptions,

            // Methods
            toggleDropdown,
            closeDropdown,
            selectOption,
            removeOption,
            clearSelection,
            getOptionLabel,
            getOptionValue,
            isSelected,
            selectHighlighted,
            navigateOptions,
            handleTab,
            handleScroll,
            handleSearchInput,
        };
    },
};
</script>

<style scoped>
.v-select {
    position: relative;
    display: block;
    width: 100%;
}

.vs__dropdown-toggle {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.vs__dropdown-toggle:hover:not(.vs--disabled .vs__dropdown-toggle) {
    border-color: #cbd5e1;
}

.vs--open .vs__dropdown-toggle {
    border-color: #9b8cff;
    box-shadow: 0 0 0 3px rgba(155, 140, 255, 0.1);
}

.vs--disabled .vs__dropdown-toggle {
    background-color: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
    opacity: 0.6;
}

.vs__selected-options {
    flex: 1;
    display: flex;
    align-items: center;
    min-height: 32px;
    overflow: hidden;
}

.vs__selected-options-container {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    width: 100%;
}

.vs__selected {
    background-color: #f8f8f8;
    color: #000;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px 8px;
    font-size: 14px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
}

.vs__placeholder {
    background-color: transparent;
    color: #9ca3af;
    font-weight: normal;
    border: none;
    padding: 0;
}

.vs__deselect,
.vs__clear {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    padding: 2px;
    transition: color 0.15s ease;
}

.vs__deselect:hover,
.vs__clear:hover {
    color: #ef4444;
}

.vs__actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.vs__open-indicator {
    color: #6b7280;
    font-size: 12px;
    transition: transform 0.2s ease;
}

.vs--open .vs__open-indicator {
    transform: rotate(180deg);
}

.vs__loading {
    color: #9b8cff;
    font-size: 14px;
}

/* Dropdown Menu - Positioned relative to parent */
.vs__dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
    max-height: 300px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.vs__search-box {
    padding: 8px;
    border-bottom: 1px solid #e2e8f0;
    background-color: #f9fafb;
}

.vs__search {
    width: 100%;
    border: none;
    outline: none;
    padding: 8px 12px;
    font-size: 14px;
    color: #111827;
    background: white;
    border-radius: 8px;
}

.vs__search:focus {
    box-shadow: 0 0 0 2px rgba(155, 140, 255, 0.1);
}

.vs__dropdown-options {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.vs__dropdown-options::-webkit-scrollbar {
    width: 6px;
}

.vs__dropdown-options::-webkit-scrollbar-track {
    background: transparent;
}

.vs__dropdown-options::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.vs__dropdown-option {
    padding: 10px 12px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
}

.vs__dropdown-option:hover,
.vs__dropdown-option--highlight {
    background-color: #f3f4f6;
}

.vs__dropdown-option--selected {
    background-color: #9b8cff;
    color: white;
    font-weight: 500;
}

.vs__dropdown-option--selected:hover {
    background-color: #8b7cef;
}

.vs__dropdown-option--create {
    background-color: #f0f9ff;
    color: #0369a1;
    font-style: italic;
}

.vs__dropdown-option--create:hover {
    background-color: #e0f2fe;
}

.vs__no-options,
.vs__loading-text {
    padding: 12px;
    font-size: 14px;
    color: #6b7280;
    text-align: center;
}

/* Transition animations */
.vs-dropdown-enter-active,
.vs-dropdown-leave-active {
    transition: all 0.2s ease;
}

.vs-dropdown-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.vs-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
    .vs__dropdown-toggle {
        background: #1f2937;
        border-color: #374151;
        color: #f3f4f6;
    }

    .vs__dropdown-menu {
        background: #1f2937;
        border-color: #374151;
    }

    .vs__search {
        background: #111827;
        color: #f3f4f6;
    }

    .vs__selected {
        background-color: #374151;
        border-color: #4b5563;
        color: #f3f4f6;
    }

    .vs__dropdown-option:hover,
    .vs__dropdown-option--highlight {
        background-color: #374151;
    }
}
</style>
